<script>
import Layout from '@layouts/main'
import TablePaginator from '@components/paginator'
import { hideValues, updateColumns } from '@utils/helpers/columns'
import customTable from '@utils/mixins/custom-table'
import paginationMethods from '@utils/mixins/pagination-methods'
import userHasRolesToWrite from '@utils/userPermissions'

import newItem from '@pages/dictionaries/organization/organization-item-new'

import { mapGetters } from 'vuex'
import AdjustColumnsDialog from '@components/adjust-columns-dialog'

export default {
  name: 'Workshops',
  metaInfo: {
    title: 'Workshops',
    meta: [{ name: 'description', content: 'Workshops' }],
  },
  components: {
    Layout,
    TablePaginator,
    newItem,
    AdjustColumnsDialog,
  },
  mixins: [customTable, paginationMethods],
  data() {
    return {
      headers: [
        { text: 'name', value: 'shortName', sortable: true, width: '10%' },
        {
          text: 'plugs',
          value: 'devices',
          sortable: true,
          width: '10%',
          show: true,
        },
        {
          text: 'description',
          value: 'description',
          sortable: false,
          width: '10%',
          show: true,
        },
        {
          text: 'city',
          value: 'city',
          sortable: true,
          width: '10%',
          show: true,
        },
        {
          text: 'region',
          value: 'region',
          sortable: true,
          width: '10%',
          show: true,
        },
        {
          text: 'delivery_type',
          value: 'deliveryType',
          sortable: true,
          show: true,
        },
        {
          text: 'status',
          value: 'status',
          sortable: true,
          width: '10%',
          show: true,
        },
      ],
      items: [],
      isLoading: false,
      page: 1,
      fab: false,
      hasFilterModal: false,
      rolesAllowedToCreate: ['SUPERUSER'],
      showFiltersModal: false,
      showCreateWarehouseModal: false,
      sortBy: 'shortName',
      modelName: 'Workshops',
      showDialog: false,
    }
  },
  computed: {
    ...mapGetters({
      workshops: 'data/getWorkshops',
    }),
    params() {
      return {
        size: this.pagination.rowsPerPage || null,
        page: this.page || null,
        sort_property: this.sortBy || null,
        sort_direction: this.descending || null,
        ...this.filters,
      }
    },
    userAllowedToCreate() {
      return userHasRolesToWrite(
        this.$store.state.auth.userRole,
        this.rolesAllowedToCreate
      )
    },
  },
  watch: {
    pagination: {
      handler() {
        this.$store.commit('pagination/SET', {
          name: 'workshops',
          pagination: {
            page: this.page,
            rowsPerPage: this.pagination.rowsPerPage,
            sortBy: this.sortBy,
            descending: this.descending,
          },
        })
      },
      deep: true,
    },
  },
  created() {
    this.getPaginator('workshops')
    this.getFilters()
    this.fetchData()
  },
  methods: {
    fetchData() {
      this.isLoading = true

      const payload = {
        params: this.params,
      }

      this.$store.dispatch('data/fetchWorkshops', payload).then(response => {
        this.totalPages = response.totalPages
        this.first = response.first
        this.last = response.last
        this.page = response.number + 1
        this.pagination = {
          ...this.pagination,
          page: 1,
          rowsPerPage: response.size,
          sortBy: this.sortBy,
          descending: this.descending,
        }
        this.isLoading = false
      })
    },
    getFilters() {
      const filter = this.$store.getters['filters/getFilters'](
        this.$route.params.documentType
      )
      this.filters = filter ? filter.value : null
    },
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        value
      )
    },
    redirectToWorkshopPage(workshopId) {
      this.$router.push(`workshops/${workshopId}`)
    },
    hideValues(field) {
      return hideValues(field, this.headers)
    },
    updateColumns(columns) {
      updateColumns(columns, this.headers)
    },
    openDialog() {
      this.showDialog = true
    },
  },
}
</script>
<template>
  <Layout>
    <div slot="actionButton">
      <div class="hidden-xs-only">
        <v-btn
          color="info"
          @click="fetchData"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn
          v-if="hasFilterModal"
          :color="filters ? `success` : `info`"
          @click="showFiltersModal = true"
        >
          <v-icon>filter_list</v-icon>
        </v-btn>
        <v-btn
          v-if="userAllowedToCreate"
          slot="activator"
          color="primary"
          dark
          @click.native.stop="showCreateWarehouseModal = true"
        >
          <v-icon>add</v-icon>
        </v-btn>
      </div>
      <div class="hidden-sm-and-up">
        <v-speed-dial
          v-model="fab"
          direction="left"
          transition="slide-y-reverse-transition"
        >
          <v-btn
            slot="activator"
            v-model="fab"
            color="blue darken-2"
            dark
            fab
            small
            flat
          >
            <v-icon>more_vert</v-icon>
            <v-icon>close</v-icon>
          </v-btn>
          <v-btn
            color="primary"
            dark
            small
            fab
            @click.native.stop="showCreateWarehouseModal = true"
          >
            <v-icon>add</v-icon>
          </v-btn>
          <v-btn
            v-if="hasFilterModal"
            :color="filters ? `success` : `info`"
            small
            fab
            @click="showFiltersModal = true"
          >
            <v-icon>filter_list</v-icon>
          </v-btn>
          <v-btn
            color="info"
            small
            fab
            @click="fetchData"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </v-speed-dial>
      </div>
    </div>
    <v-layout justify-end>
      <v-btn
        icon
        flat
        @click="openDialog()">
        <v-icon>view_column</v-icon>
      </v-btn>
    </v-layout>
    <v-data-table
      :headers="headers"
      :items="workshops"
      :class="$style.sdTable"
      :pagination="pagination"
      :loading="isLoading"
      hide-actions
    >
      <template
        slot="headers"
        slot-scope="{ headers }">
        <tr>
          <th
            v-for="header in headers"
            :key="header.text"
            :class="getSortClass(header)"
            :style="[header.show === false ? {'display': 'none'} : {'text-align': 'left'}]"
            @click="changeSort(header)"
          >
            {{ $t(`label__${header.text}`) }}
            <v-icon
              v-if="header.sortable"
              small>expand_less</v-icon>
          </th>
        </tr>
      </template>
      <v-progress-linear
        slot="progress"
        height="2"
        color="blue"
        background-color="#E0E0E0"
        indeterminate
      />
      <!-- eslint-disable-next-line no-unused-vars -->
      <template
        slot="items"
        slot-scope="{ item }"
      >
        <tr
          :class="$style.sdEditRow"
          @click="redirectToWorkshopPage(item.id)">
          <td>{{ item.shortName }}</td>
          <td v-if="!hideValues('devices')">{{ item.devices }}</td>
          <td v-if="!hideValues('description')">{{ item.description }}</td>
          <td v-if="!hideValues('city')">{{ getValue('city', item.city) }}</td>
          <td v-if="!hideValues('region')">{{ getValue('region', item.region) }}</td>
          <td v-if="!hideValues('deliveryType')">{{ $t(`deliverytype.${item.deliveryType}.name`) }}</td>
          <td v-if="!hideValues('status')">{{ $t(`organizationstatus.${item.status}.name`) }}</td>
        </tr>
      </template>
    </v-data-table>
    <table-paginator
      :page="page"
      :total-pages="totalPages"
      :first="first"
      :last="last"
      :rows-per-page="pagination.rowsPerPage"
      @change-page="changePage($event)"
      @change-rows-per-page="changeRowsPerPage($event)" />
    <adjust-columns-dialog
      v-model="showDialog"
      :headers="headers.filter(h => h.show !== undefined)"
      :model-name="modelName"
      @columns-updated="updateColumns"
    />
    <new-item
      :modal-title="$t('titles__add_workshop')"
      :const-props="{'type': 3}"
      :new-item="showCreateWarehouseModal"
      organization-initial-type="workshops"
      @close-modal="showCreateWarehouseModal = false" />
  </Layout>
</template>
<style lang="scss" module>
@import '@design';
.sdTable {
  border: 1px solid #dfdfdf;

  th {
    outline: none;
  }
}
.sdEditRow {
  cursor: pointer;
}
.sdBorder {
  border: 1px solid #dfdfdf;
  border-bottom: none;
}
</style>
